import React from 'react';
import {
  Card,
  List,
  Skeleton,
  Stack,
  SxProps,
  Theme,
} from '@mui/material';
import {
  ConversationSummary,
} from '@marageti/z4-sdk/lib/travel';
import ConversationLink, { ConversationLinkProps } from './ConversationLink';
import { ConversationLinkMap, ConversationSummaryDetails, HybridConversationLink } from './types';

type RenderConversationLink = ({
  conversationSummary,
  tripMatchId,
  tripRequestId,
}: {
  conversationSummary: ConversationSummary,
  tripMatchId?: string;
  tripRequestId?: string;
}) => string;

export type ConversationLinksCardProps = {
  conversationSummaries: ConversationLinkMap
  tripMatchId?: string;
  tripRequestId: string;
  getConversationTypeTitle: ConversationLinkProps['getConversationTypeTitle']
  renderConversationLink: RenderConversationLink
  getConversationIcon?: ConversationLinkProps['getConversationIcon']
  sx?: SxProps<Theme>
};

const getConversationInContext = (
  conversationSummaryDetails: HybridConversationLink,
  tripMatchId: string | undefined,
  tripRequestId: string,
  renderConversationLink: RenderConversationLink,
): ConversationSummaryDetails => {
  // First check agent context, then team member context, then default context
  if (conversationSummaryDetails.agentContext?.hasNewMessages) {
    return {
      to: renderConversationLink({
        conversationSummary: conversationSummaryDetails.agentContext,
        tripMatchId,
        tripRequestId,
      }),
      conversationSummary: conversationSummaryDetails.agentContext,
      badgeColor: 'warning',
    };
  }
  if (conversationSummaryDetails.teamMemberContext?.hasNewMessages) {
    return {
      to: renderConversationLink({
        conversationSummary: conversationSummaryDetails.teamMemberContext,
        tripMatchId,
        tripRequestId,
      }),
      conversationSummary: conversationSummaryDetails.teamMemberContext,
      badgeColor: 'info',
    };
  }
  return {
    to: renderConversationLink({
      conversationSummary: conversationSummaryDetails.defaultContext,
      tripMatchId,
      tripRequestId,
    }),
    conversationSummary: conversationSummaryDetails.defaultContext,
  };
};

const ConversationLinksCard = ({
  conversationSummaries,
  tripMatchId,
  tripRequestId,
  getConversationTypeTitle,
  renderConversationLink,
  getConversationIcon,
  sx,
}: ConversationLinksCardProps) => {
  const conversationLinksToRender = Object.entries(conversationSummaries).map(
    ([, conversationSummaryDetails]) => getConversationInContext(
      conversationSummaryDetails,
      tripMatchId,
      tripRequestId,
      renderConversationLink,
    ),
  );

  if (!conversationLinksToRender.length) {
    return null;
  }

  return (
    <Card sx={{ p: 3, ...sx }}>
      <List component={Stack} spacing={2} disablePadding>
        {conversationLinksToRender.map(({ to, badgeColor, conversationSummary }) => (
          <ConversationLink
            key={conversationSummary.conversationId}
            to={to}
            badgeColor={badgeColor}
            conversationSummary={conversationSummary}
            getConversationTypeTitle={getConversationTypeTitle}
            getConversationIcon={getConversationIcon}
          />
        ))}
      </List>
    </Card>
  );
};

ConversationLinksCard.Skeleton = ({ sx }: { sx?: SxProps<Theme> }) => (
  <Card sx={{ p: 3, ...sx }}>
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} flexGrow={1}>
        <Skeleton variant="circular" height={40} width={40} />
        <Skeleton variant="rounded" height={40} width={400} />
      </Stack>
      <Stack direction="row" spacing={2} flexGrow={1}>
        <Skeleton variant="circular" height={40} width={40} />
        <Skeleton variant="rounded" height={40} width={400} />
      </Stack>
    </Stack>
  </Card>
);
export default ConversationLinksCard;
